import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCreditCalculationType } from "./enums/enumCreditCalculationType.generated";

// Model Objects

export class CreditTerm extends ModelObjectWithDirtyCheck<CreditTerm> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Day or Days for Terms Calculation
    */
    public calculationDay: number = 0;
    /**
    * Terms Calculation Code : OFM=Of Following Month, DAI=Days After Invoice, DAM=Days After Invoice Month, OCM=Of Current Month
    */
    public calculationType: EnumCreditCalculationType = EnumCreditCalculationType.none;
    /**
    * Credit Terms Description
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Show Balance on Delivery Notes
    */
    public showBalance: boolean = false;
    /**
    * Accounts Analysis Code
    * Not Nullable
    * Max Length : 1000
    */
    public accountsAnalysisCode: string = "";
    public isHidden: boolean = false;
    /**
    * Whether to allow pay on account in Trade Portal
    */
    public allowTradePortalPayOnAccount: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.calculationDay = jsonSerializedObject.calculationDay;
          this.calculationType = EnumCreditCalculationType.getByValue(jsonSerializedObject.calculationType, this.calculationType);
          this.name = jsonSerializedObject.name;
          this.showBalance = jsonSerializedObject.showBalance;
          this.accountsAnalysisCode = jsonSerializedObject.accountsAnalysisCode;
          this.isHidden = jsonSerializedObject.isHidden;
          this.allowTradePortalPayOnAccount = jsonSerializedObject.allowTradePortalPayOnAccount;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.calculationDay = this.calculationDay;
        toRet.calculationType = this.calculationType.value;
        toRet.name = this.name;
        toRet.showBalance = this.showBalance;
        toRet.accountsAnalysisCode = this.accountsAnalysisCode;
        toRet.isHidden = this.isHidden;
        toRet.allowTradePortalPayOnAccount = this.allowTradePortalPayOnAccount;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CreditTerm.Id cannot be empty"},
            {field: "calculationDay", rule: (v: any) => v == null || v >= -2147483648 || "CalculationDay is not greater than minimum allowed value: -2147483648"},
            {field: "calculationDay", rule: (v: any) => v == null || v <= 2147483647 || "CalculationDay is above maximum allowed value: 2147483647"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "accountsAnalysisCode", rule: (v: any) => v == null || v.length <= 1000 || "AccountsAnalysisCode has too many characters (1000)"},
    ];
}


export class CreditTermService extends ServiceBase {

  public async getAllCreditTerms(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CreditTerm)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.creditTerms.map((v: any) => new CreditTerm(v));;
    return this.makeJsonRequest("creditTerm/v1/getAllCreditTerms", req, false, extractor, opts);
  };

  public async getCreditTerm(creditTermId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CreditTerm> {
    const req = {
    tenantId:api.tenantId.toString(),
    creditTermId:creditTermId.toString(),
    };
    const extractor = (response:any) => new CreditTerm(response.creditTerm);;
    return this.makeJsonRequest("creditTerm/v1/getCreditTerm", req, false, extractor, opts);
  };

  public async getCreditTerms(creditTermIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CreditTerm)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    creditTermIds:creditTermIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.creditTerms.map((v: any) => new CreditTerm(v));;
    return this.makeJsonRequest("creditTerm/v1/getCreditTerms", req, false, extractor, opts);
  };

  public async saveCreditTerm(creditTerm: CreditTerm, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    creditTerm:creditTerm.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("creditTerm/v1/saveCreditTerm", req, true, extractor, opts);
  };

  public async setCreditTermIsHidden(creditTermId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    creditTermId:creditTermId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("creditTerm/v1/setCreditTermIsHidden", req, true, extractor, opts);
  };
}

export const creditTermService = new CreditTermService();